import React, { useState, useEffect } from "react";
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Search,
  Shield,
  UserCheck,
  UserX,
  X,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Crown,
  Mail,
  Copy,
  Check,
  Clock,
  Link,
  ExternalLink,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import {
  logCreate,
  logUpdate,
  logDelete,
  AUDIT_TYPES,
} from "../lib/auditLogger";

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadUsersFromDB = async (organisationId) => {
  const { data, error } = await supabase
    .from("profils")
    .select("*")
    .eq("organisation_id", organisationId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return data || [];
};

const loadInvitationsFromDB = async (organisationId) => {
  const { data, error } = await supabase
    .from("invitations")
    .select("*")
    .eq("organisation_id", organisationId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });

  if (error) {
    // Table n'existe peut-être pas encore
    console.warn("Table invitations non disponible:", error.message);
    return [];
  }
  return data || [];
};

const createInvitation = async (invitationData) => {
  // Générer un token unique
  const token = crypto.randomUUID() + "-" + Date.now().toString(36);

  const { data, error } = await supabase
    .from("invitations")
    .insert([
      {
        ...invitationData,
        token,
        status: "pending",
        expires_at: new Date(
          Date.now() + 7 * 24 * 60 * 60 * 1000
        ).toISOString(), // 7 jours
      },
    ])
    .select()
    .single();

  if (error) throw error;
  return data;
};

const cancelInvitation = async (invitationId) => {
  const { error } = await supabase
    .from("invitations")
    .update({ status: "cancelled" })
    .eq("id", invitationId);

  if (error) throw error;
  return true;
};

const updateUserInDB = async (id, updates) => {
  const { data, error } = await supabase
    .from("profils")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const deleteUserFromDB = async (id) => {
  const { error } = await supabase.from("profils").delete().eq("id", id);

  if (error) throw error;
  return true;
};

// ============================================
// COMPOSANTS UI
// ============================================

const LoadingSpinner = ({ message = "Chargement..." }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <Loader2 size={40} className="text-sky-600 animate-spin mb-4" />
    <p className="text-gray-500 dark:text-gray-400">{message}</p>
  </div>
);

const ErrorMessage = ({ message, onRetry }) => (
  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
    <AlertTriangle size={40} className="text-red-500 mx-auto mb-4" />
    <p className="text-red-700 dark:text-red-300 font-medium mb-4">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 inline-flex items-center gap-2"
      >
        <RefreshCw size={16} /> Réessayer
      </button>
    )}
  </div>
);

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const UsersManagement = ({ showToast, entrepriseId }) => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [invitationsTableExists, setInvitationsTableExists] = useState(true);

  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    role: "membre",
  });

  const [inviteData, setInviteData] = useState({
    email: "",
    role: "membre",
  });

  const roles = {
    admin: {
      label: "Administrateur",
      color:
        "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
      permissions: "Accès complet",
    },
    membre: {
      label: "Membre",
      color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      permissions: "Accès standard",
    },
    lecteur: {
      label: "Lecteur",
      color: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      permissions: "Lecture seule",
    },
  };

  // Charger les utilisateurs et invitations
  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const usersData = await loadUsersFromDB(entrepriseId);
      setUsers(usersData);

      // Charger les invitations (si table existe)
      try {
        const invitationsData = await loadInvitationsFromDB(entrepriseId);
        setInvitations(invitationsData);
        setInvitationsTableExists(true);
      } catch (err) {
        console.warn("Invitations table not ready:", err);
        setInvitationsTableExists(false);
      }
    } catch (err) {
      setError("Erreur lors du chargement des utilisateurs.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filtrage
  const filteredUsers = users.filter((user) => {
    const fullName = `${user.prenom || ""} ${user.nom || ""}`.toLowerCase();
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      (user.email || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === "all" || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // Stats
  const stats = {
    total: users.length,
    admins: users.filter((u) => u.role === "admin").length,
    membres: users.filter((u) => u.role === "membre").length,
    lecteurs: users.filter((u) => u.role === "lecteur").length,
    pending: invitations.length,
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      prenom: user.prenom || "",
      nom: user.nom || "",
      email: user.email || "",
      role: user.role || "membre",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (editingUser) {
        const updated = await updateUserInDB(editingUser.id, {
          prenom: formData.prenom,
          nom: formData.nom,
          role: formData.role,
        });
        setUsers(users.map((u) => (u.id === editingUser.id ? updated : u)));
        showToast("Utilisateur modifié avec succès!", "success");
      }
      setShowModal(false);
    } catch (err) {
      showToast("Erreur lors de l'enregistrement.", "error");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Vérifier si l'email existe déjà
      const existingUser = users.find(
        (u) => u.email?.toLowerCase() === inviteData.email.toLowerCase()
      );
      if (existingUser) {
        showToast(
          "Cet utilisateur fait déjà partie de l'organisation.",
          "error"
        );
        setIsSaving(false);
        return;
      }

      // Vérifier si une invitation existe déjà
      const existingInvite = invitations.find(
        (i) => i.email?.toLowerCase() === inviteData.email.toLowerCase()
      );
      if (existingInvite) {
        showToast(
          "Une invitation est déjà en attente pour cet email.",
          "warning"
        );
        setIsSaving(false);
        return;
      }

      // Récupérer les infos de l'utilisateur actuel
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      const currentProfile = users.find((u) => u.id === currentUser?.id);

      // Créer l'invitation
      const invitation = await createInvitation({
        organisation_id: entrepriseId,
        email: inviteData.email,
        role: inviteData.role,
        invited_by: currentUser?.id,
        invited_by_name: currentProfile
          ? `${currentProfile.prenom} ${currentProfile.nom}`
          : "Admin",
      });

      // Générer le lien d'invitation
      const baseUrl = window.location.origin;
      const inviteLink = `${baseUrl}/invitation?token=${invitation.token}`;

      setInviteSuccess({
        email: inviteData.email,
        link: inviteLink,
        token: invitation.token,
      });

      // Rafraîchir les invitations
      setInvitations([invitation, ...invitations]);

      // Logger l'activité
      await logCreate(entrepriseId, AUDIT_TYPES.INVITATION, inviteData.email, {
        role: inviteData.role,
        expires_at: invitation.expires_at,
      });

      showToast(`Invitation créée pour ${inviteData.email}!`, "success");
    } catch (err) {
      console.error("Erreur invitation:", err);

      // Si la table n'existe pas, montrer le SQL à exécuter
      if (
        err.message?.includes("relation") &&
        err.message?.includes("does not exist")
      ) {
        showToast(
          "La table 'invitations' n'existe pas encore. Exécutez le SQL fourni.",
          "error"
        );
        setInvitationsTableExists(false);
      } else {
        showToast(`Erreur: ${err.message}`, "error");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await cancelInvitation(invitationId);
      setInvitations(invitations.filter((i) => i.id !== invitationId));
      showToast("Invitation annulée.", "success");
    } catch (err) {
      showToast("Erreur lors de l'annulation.", "error");
      console.error(err);
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    showToast("Lien copié!", "success");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find((u) => u.id === userId);

    // Empêcher la suppression du créateur (premier utilisateur)
    if (users.length > 0 && users[0].id === userId) {
      showToast(
        "Impossible de supprimer le créateur de l'organisation.",
        "error"
      );
      setShowDeleteConfirm(null);
      return;
    }

    // Empêcher la suppression du dernier admin
    if (user?.role === "admin" && stats.admins <= 1) {
      showToast("Impossible de supprimer le dernier administrateur.", "error");
      setShowDeleteConfirm(null);
      return;
    }

    try {
      await deleteUserFromDB(userId);
      setUsers(users.filter((u) => u.id !== userId));

      // Logger l'activité
      await logDelete(
        entrepriseId,
        AUDIT_TYPES.UTILISATEUR,
        `${user?.prenom} ${user?.nom}`,
        {
          email: user?.email,
          role: user?.role,
        }
      );

      showToast("Utilisateur supprimé.", "success");
    } catch (err) {
      showToast("Erreur lors de la suppression.", "error");
      console.error(err);
    }
    setShowDeleteConfirm(null);
  };

  const handleChangeRole = async (userId, newRole) => {
    // Empêcher le changement de rôle du créateur
    if (users.length > 0 && users[0].id === userId) {
      showToast("Le créateur de l'organisation reste administrateur.", "error");
      return;
    }

    try {
      const userToUpdate = users.find((u) => u.id === userId);
      const oldRole = userToUpdate?.role;

      const updated = await updateUserInDB(userId, { role: newRole });
      setUsers(users.map((u) => (u.id === userId ? updated : u)));

      // Logger l'activité
      await logUpdate(
        entrepriseId,
        AUDIT_TYPES.UTILISATEUR,
        `${userToUpdate?.prenom} ${userToUpdate?.nom}`,
        {
          oldRole,
          newRole,
          email: userToUpdate?.email,
        }
      );

      showToast("Rôle modifié avec succès!", "success");
    } catch (err) {
      showToast("Erreur lors du changement de rôle.", "error");
      console.error(err);
    }
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteSuccess(null);
    setInviteData({ email: "", role: "membre" });
  };

  // États de chargement et erreur
  if (!entrepriseId) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-amber-500 mx-auto mb-4" />
        <p className="text-amber-700 dark:text-amber-300">
          Veuillez sélectionner une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading)
    return <LoadingSpinner message="Chargement des utilisateurs..." />;
  if (error) return <ErrorMessage message={error} onRetry={loadData} />;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            Gestion des utilisateurs
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Gérez les membres de votre organisation
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Actualiser"
          >
            <RefreshCw size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 flex items-center gap-2 shadow-lg"
          >
            <Mail size={18} />
            <span>Inviter</span>
          </button>
        </div>
      </div>

      {/* Alerte si table invitations n'existe pas */}
      {!invitationsTableExists && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
            ⚠️ Table 'invitations' non trouvée
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-400 mb-3">
            Exécutez le SQL suivant dans Supabase pour activer les invitations :
          </p>
          <code className="block bg-amber-100 dark:bg-amber-900/40 p-3 rounded text-xs overflow-x-auto">
            /mnt/user-data/outputs/moneris/sql/003_create_invitations_table.sql
          </code>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-sky-500">
          <div className="flex items-center gap-3">
            <Users className="text-sky-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-purple-500">
          <div className="flex items-center gap-3">
            <Shield className="text-purple-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.admins}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Admins</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center gap-3">
            <UserCheck className="text-blue-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.membres}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Membres
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-gray-500">
          <div className="flex items-center gap-3">
            <UserX className="text-gray-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.lecteurs}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Lecteurs
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 border-l-4 border-amber-500">
          <div className="flex items-center gap-3">
            <Clock className="text-amber-500" size={24} />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.pending}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                En attente
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invitations en attente */}
      {invitations.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-3 flex items-center gap-2">
            <Clock size={18} />
            Invitations en attente ({invitations.length})
          </h3>
          <div className="space-y-2">
            {invitations.map((inv) => {
              const inviteLink = `${window.location.origin}/invitation?token=${inv.token}`;
              return (
                <div
                  key={inv.id}
                  className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-amber-500" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {inv.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {roles[inv.role]?.label} • Expire le{" "}
                        {new Date(inv.expires_at).toLocaleDateString("fr-CA")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopyLink(inviteLink)}
                      className="p-2 text-sky-600 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded-lg"
                      title="Copier le lien"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => handleCancelInvitation(inv.id)}
                      className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                      title="Annuler"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Rechercher un utilisateur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
          >
            <option value="all">Tous les rôles</option>
            <option value="admin">Administrateurs</option>
            <option value="membre">Membres</option>
            <option value="lecteur">Lecteurs</option>
          </select>
        </div>
      </div>

      {/* Liste des utilisateurs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users
              size={48}
              className="text-gray-300 dark:text-gray-600 mx-auto mb-4"
            />
            <p className="text-gray-500 dark:text-gray-400">
              Aucun utilisateur trouvé
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    Utilisateur
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    Rôle
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    Membre depuis
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user, index) => {
                  const isCreator = index === 0;
                  return (
                    <tr
                      key={user.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                            {(user.prenom?.[0] || "?").toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {user.prenom} {user.nom}
                              </span>
                              {isCreator && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-xs rounded-full">
                                  <Crown size={12} />
                                  Créateur
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {user.email}
                      </td>
                      <td className="px-6 py-4">
                        {isCreator ? (
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${roles.admin.color}`}
                          >
                            {roles.admin.label}
                          </span>
                        ) : (
                          <select
                            value={user.role || "membre"}
                            onChange={(e) =>
                              handleChangeRole(user.id, e.target.value)
                            }
                            className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${
                              roles[user.role]?.color || roles.membre.color
                            }`}
                          >
                            <option value="lecteur">Lecteur</option>
                            <option value="membre">Membre</option>
                            <option value="admin">Administrateur</option>
                          </select>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(user.created_at).toLocaleDateString("fr-CA")}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-2 text-sky-600 hover:bg-sky-100 dark:hover:bg-sky-900/30 rounded-lg"
                            title="Modifier"
                          >
                            <Edit2 size={18} />
                          </button>
                          {!isCreator && (
                            <button
                              onClick={() => setShowDeleteConfirm(user.id)}
                              className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                              title="Supprimer"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Modifier */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white p-6 rounded-t-xl flex items-center justify-between">
              <h2 className="text-xl font-bold">Modifier l'utilisateur</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:bg-slate-600 p-2 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) =>
                      setFormData({ ...formData, prenom: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) =>
                      setFormData({ ...formData, nom: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg opacity-50 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  L'email ne peut pas être modifié.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving && <Loader2 size={18} className="animate-spin" />}
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Invitation */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full">
            <div className="bg-gradient-to-r from-sky-600 to-indigo-600 text-white p-6 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail size={24} />
                <h2 className="text-xl font-bold">Inviter un utilisateur</h2>
              </div>
              <button
                onClick={closeInviteModal}
                className="text-white hover:bg-white/20 p-2 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            {inviteSuccess ? (
              // Succès - Afficher le lien
              <div className="p-6 space-y-4">
                <div className="text-center mb-4">
                  <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                    <Check
                      size={32}
                      className="text-green-600 dark:text-green-400"
                    />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Invitation créée!
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">
                    Partagez ce lien avec <strong>{inviteSuccess.email}</strong>
                  </p>
                </div>

                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lien d'invitation
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={inviteSuccess.link}
                      className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                    />
                    <button
                      onClick={() => handleCopyLink(inviteSuccess.link)}
                      className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2"
                    >
                      {copiedLink ? <Check size={18} /> : <Copy size={18} />}
                      {copiedLink ? "Copié!" : "Copier"}
                    </button>
                  </div>
                </div>

                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Note:</strong> Ce lien expire dans 7 jours.
                    L'utilisateur pourra créer son compte et rejoindre
                    automatiquement votre organisation.
                  </p>
                </div>

                <div className="flex justify-end pt-4 border-t dark:border-gray-700">
                  <button
                    onClick={closeInviteModal}
                    className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              // Formulaire d'invitation
              <form onSubmit={handleInviteUser} className="p-6 space-y-4">
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Créez un lien d'invitation pour ajouter un nouveau membre à
                  votre organisation.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Adresse courriel *
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteData.email}
                    onChange={(e) =>
                      setInviteData({ ...inviteData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
                    placeholder="nouveau.membre@entreprise.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rôle
                  </label>
                  <select
                    value={inviteData.role}
                    onChange={(e) =>
                      setInviteData({ ...inviteData, role: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
                  >
                    <option value="lecteur">Lecteur (Lecture seule)</option>
                    <option value="membre">Membre (Accès standard)</option>
                    <option value="admin">
                      Administrateur (Accès complet)
                    </option>
                  </select>
                </div>

                <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg p-4">
                  <p className="text-sm text-sky-700 dark:text-sky-400">
                    🔗 Un lien d'invitation unique sera généré. Partagez-le avec
                    la personne pour qu'elle puisse créer son compte.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeInviteModal}
                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving || !invitationsTableExists}
                    className="px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSaving && <Loader2 size={18} className="animate-spin" />}
                    <Link size={18} />
                    Créer l'invitation
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                <Trash2 className="text-red-600 dark:text-red-400" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Supprimer l'utilisateur ?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Cette action est irréversible. L'utilisateur perdra l'accès
                immédiatement.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDeleteUser(showDeleteConfirm)}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
